import Header from "components/Headers/Header";
import { ErrorMessage, Formik } from "formik";
import React, { useState } from "react";
import ProtectedRoute from "components/ProtectedRoute";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    FormGroup,
    Input,
    Row,
} from "reactstrap";
import Select from "react-select";
import { createPlatform } from "utils/endpoint";
import * as yup from "yup";
import clsx from "clsx";
import UseFullPageLoader from "utils/UseFullPageLoader";
import { isActive, publisherType as countryType } from "utils/enums";

const validationSchema = yup.object({
    name: yup.string().required("Name is required"),
    country: yup.string().required("Country is required"),
    weblink: yup.string().url("Enter a valid URL").nullable(),
});

function CreatePlatform() {
    const [loader, showLoader, hideLoader] = UseFullPageLoader();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    return (
        <Formik
            enableReinitialize
            initialValues={{
                name: "",
                country: "",
                weblink: "",
                is_active: true,
            }}
            validationSchema={validationSchema}
            onSubmit={async (values, { resetForm, setSubmitting }) => {
                setLoading(true);
                showLoader()
                const $inputData = {
                    name: values.name,
                    country: values.country,
                    weblink: values.weblink,
                    is_active: values.is_active,
                };
                const result = await createPlatform($inputData);
                if (result?.isSuccess) {
                    toast.success(`${result.message}`);
                    setLoading(false);
                    hideLoader()
                    resetForm();
                    navigate("/admin/platform/platform-list");
                } else {
                    setSubmitting(false);
                    setLoading(false);
                    hideLoader()
                    return toast.error(
                        result.message || `Something went wrong, please try again later!`
                    );
                }
            }}>
            {({
                handleChange,
                handleSubmit,
                values,
                errors,
                touched,
                setFieldValue,
            }) => (
                <form onSubmit={handleSubmit} autoComplete="off">
                    <>
                        <Header />
                        <Container className="mt--7" fluid>
                            <Row>
                                <div className="col">
                                    <Card className="shadow">
                                        <CardHeader className="border-0">
                                            <Row>
                                                <Col lg="6">
                                                    <h1 className="mb-0">Create Platform</h1>
                                                </Col>
                                                <Col lg="6" className="add_btn text-right">
                                                    <Button
                                                        color="primary"
                                                        className=""
                                                        onClick={() => {
                                                            navigate("/admin/platform/platform-list");
                                                        }}>
                                                        <span className="me-2">Back</span>
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </CardHeader>
                                        <CardBody>
                                            <Row>
                                                <Col lg="3">
                                                    <FormGroup>
                                                        <label className="form-control-label required">
                                                            Name
                                                        </label>
                                                        <Input
                                                            className={clsx("form-control", {
                                                                "is-invalid":
                                                                    Boolean(errors.name) && touched.name,
                                                            })}
                                                            id="name"
                                                            name="name"
                                                            placeholder="Name"
                                                            type="text"
                                                            value={values.name}
                                                            onChange={handleChange}
                                                        />
                                                        <div className="text-danger text-align-top-center mt-1">
                                                            <ErrorMessage name="name" />
                                                        </div>
                                                    </FormGroup>
                                                </Col>
                                                <Col lg="3">
                                                    <FormGroup>
                                                        <label className="form-control-label required">
                                                            Country
                                                        </label>
                                                        <Select
                                                            name="country"
                                                            id="country"
                                                            className={clsx({
                                                                "is-invalid":
                                                                    Boolean(errors.country) && touched.country,
                                                            })}
                                                            options={Array.from(countryType).map((value) => {
                                                                return {
                                                                    value: value.id,
                                                                    label: value.name,
                                                                };
                                                            })}
                                                            onChange={(event) => {
                                                                setFieldValue("country", event.value);
                                                            }}
                                                        />
                                                        <div className="text-danger text-align-top-center mt-1">
                                                            <ErrorMessage name="country" />
                                                        </div>
                                                    </FormGroup>
                                                </Col>
                                                <Col lg="3">
                                                    <FormGroup>
                                                        <label className="form-control-label">
                                                            Weblink
                                                        </label>
                                                        <Input
                                                            className={clsx("form-control", {
                                                                "is-invalid":
                                                                    Boolean(errors.weblink) && touched.weblink,
                                                            })}
                                                            id="weblink"
                                                            name="weblink"
                                                            placeholder="https://example.com"
                                                            type="text"
                                                            value={values.weblink}
                                                            onChange={handleChange}
                                                        />
                                                        <div className="text-danger text-align-top-center mt-1">
                                                            <ErrorMessage name="weblink" />
                                                        </div>
                                                    </FormGroup>
                                                </Col>
                                                <Col lg="3">
                                                    <FormGroup>
                                                        <label className="form-control-label">
                                                            Is Active
                                                        </label>
                                                        <Select
                                                            name="is_active"
                                                            id="is_active"
                                                            defaultValue={{ label: "Yes", value: true }}
                                                            options={Array.from(isActive).map((value) => {
                                                                return {
                                                                    value: value.id,
                                                                    label: value.name,
                                                                };
                                                            })}
                                                            onChange={(event) => {
                                                                setFieldValue("is_active", event.value);
                                                            }}
                                                        />
                                                    </FormGroup>
                                                </Col>
                                            </Row>
                                            <Row>
                                                <Col lg="3">
                                                    <Button
                                                        type="submit"
                                                        className="my-3 w-100"
                                                        color="primary">
                                                        {loading && (
                                                            <span
                                                                className="indicator-progress"
                                                                style={{ display: "block" }}>
                                                                Please Wait..
                                                                <span className="spinner-border spinner-border-sm align-middle ms-3 mb-1"></span>
                                                            </span>
                                                        )}
                                                        {!loading && (
                                                            <span className="indicator-label">
                                                                Create Platform
                                                            </span>
                                                        )}
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </CardBody>
                                    </Card>
                                </div>
                            </Row>
                        </Container>
                        {loader}
                    </>
                </form>
            )}
        </Formik>
    );
}

export default (props) => (
    <ProtectedRoute requiredPermissions="create-platform">
        <CreatePlatform {...props} />
    </ProtectedRoute>
);
